// Bridges the base's cell/edge geometry (base-walls / base-topology) to the
// generic tactical nav grid (tactical-pathfinding). A base defense plays out
// *inside* the base building, so unlike a raid — where every building is a
// solid blocked obstacle — the base interior must be walkable, its walls must
// block, and its doorways must be openings that combat can later gate by HP.
//
// The unified "obstacle field" here is the single source the nav grid, the
// vision occluders (Phase A), and the field-object HP all read from, so those
// three views of a door never drift apart (see BASE_DEFENSE_RAID_REBUILD_PLAN).

import { baseInteriorTest, doorwaySides, latticeEndpoints, makeToWorld } from "./base-defense-geometry.mjs";
import { buildNavGrid } from "./tactical-pathfinding.mjs";
import { cellKey, exteriorBoundaryEdges, isExteriorEdge } from "./base-walls.mjs";
import { DEFAULT_DOOR_HP } from "./base-topology.mjs";
import { ENEMY_STATES, ENEMY_TYPES } from "./tactical-enemy-ai.mjs";
import { ENEMY_SPEED_METERS_PER_GAME_MINUTE } from "./tactical-session.mjs";
import { facingBearingDegrees } from "./vision-cone.mjs";
import { distanceInMeters } from "../road-graph.mjs";

// Small deterministic offsets (in cells) so several defenders sharing a room
// don't stack on the exact centroid. First is the centroid itself, then a ring.
const SPREAD_OFFSETS = [
  [0, 0], [1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [-1, -1], [1, -1], [-1, 1],
];

// A base's walls and doors as world-space segments, doors carrying their live
// HP/state from the topology (matched by edgeKey). Walls are permanent
// blockers; doors are destructible openings — the same entity a barricade will
// be. `interiorTest(coordinate)` answers "is this point inside the base?".
//
// wallState: { wallEdgeKeys, doorEdgeKeys } (base-walls). topology: optional
// { doorways: [{ edgeKey, hp, maxHp, state }] } (base-topology) — when absent,
// doors default to intact at unknown HP (geometry still tags them as openings).
export function baseObstacleField(shape, wallState, topology = null) {
  const toWorld = makeToWorld(shape);
  const segment = (edgeKey) => {
    const { a, b } = latticeEndpoints(edgeKey);
    return { edgeKey, a: toWorld(a[0], a[1]), b: toWorld(b[0], b[1]) };
  };

  const doorEdgeKeys = new Set(wallState?.doorEdgeKeys || []);
  const doorwayByEdge = new Map(
    (topology?.doorways || [])
      .filter((doorway) => doorway.edgeKey)
      .map((doorway) => [doorway.edgeKey, doorway]),
  );

  // The base's outer boundary is IMPLICITLY walled everywhere it isn't a door
  // (base-walls: "exterior blocking is implicit and never tracked" — so
  // generateBaseLayout leaves it out of wallEdgeKeys). The nav grid + occluders
  // can't rely on an implicit wall, so materialize the exterior boundary here;
  // without it a raider walks straight through the perimeter (only interior
  // walls would block), reaching the safe room without breaching a door.
  const wallEdgeKeys = new Set([...(wallState?.wallEdgeKeys || []), ...exteriorBoundaryEdges(cellsOf(shape))]);

  const walls = [];
  for (const edgeKey of wallEdgeKeys) {
    if (doorEdgeKeys.has(edgeKey)) continue; // a wall cut into a door is a door, not a wall
    walls.push(segment(edgeKey));
  }

  const doors = [];
  for (const edgeKey of doorEdgeKeys) {
    const doorway = doorwayByEdge.get(edgeKey);
    doors.push({
      ...segment(edgeKey),
      hp: doorway?.hp ?? null,
      maxHp: doorway?.maxHp ?? null,
      state: doorway?.state ?? "intact",
    });
  }

  return { walls, doors, interiorTest: baseInteriorTest(shape) };
}

// The set of door edgeKeys currently breached (HP <= 0). The nav grid tags door
// cells with their edgeKey; enemies path with doorPassable = "is this door
// breached?", so an intact door blocks their approach until it falls.
export function breachedDoorEdges(field) {
  return new Set((field?.doors || []).filter((door) => door.hp != null && door.hp <= 0).map((door) => door.edgeKey));
}

// Where each defender starts a base defense: spread across the base's rooms
// (round-robin over wall-bounded, non-corridor rooms) at their centroids, with
// a small per-defender offset so co-garrisoned survivors don't overlap. Every
// survivor goes on the map — no 3-unit party cap. Returns world coordinates in
// roster order.
export function defenderSpawnPositions(shape, rooms, count, { spacingCells = 0.7 } = {}) {
  if (!Number.isFinite(count) || count <= 0) return [];
  const usable = (rooms || []).filter((room) => !room.isCorridor && room.cellKeys?.size);
  const pool = usable.length ? usable : (rooms || []).filter((room) => room.cellKeys?.size);
  if (!pool.length) return [];

  const toWorld = makeToWorld(shape);
  const centroids = pool.map((room) => {
    let sumCol = 0, sumRow = 0;
    for (const key of room.cellKeys) { const [row, col] = key.split(":").map(Number); sumCol += col + 0.5; sumRow += row + 0.5; }
    const n = room.cellKeys.size;
    return [sumCol / n, sumRow / n];
  });

  const placedPerRoom = new Array(pool.length).fill(0);
  const positions = [];
  for (let i = 0; i < count; i += 1) {
    const roomIndex = i % pool.length;
    const slot = placedPerRoom[roomIndex];
    placedPerRoom[roomIndex] += 1;
    const [dc, dr] = SPREAD_OFFSETS[slot % SPREAD_OFFSETS.length];
    const [col, row] = centroids[roomIndex];
    positions.push(toWorld(col + dc * spacingCells, row + dr * spacingCells));
  }
  return positions;
}

// Build a nav grid for a base defense: the base interior is walkable, its walls
// block, and its doorways are HP-gateable openings. `cellSizeMeters` defaults
// finer than a raid's 4 m because a base is only tens of metres across and its
// ~3 m cells need to span several nav cells to leave rooms walkable.
export function buildBaseNavGrid(zone, { shape, wallState, topology = null, cellSizeMeters = 0.8, ...rest } = {}) {
  const field = baseObstacleField(shape, wallState, topology);
  const grid = buildNavGrid(zone, {
    cellSizeMeters,
    interior: field.interiorTest,
    wallSegments: field.walls,
    doorSegments: field.doors,
    ...rest,
  });
  return { grid, field };
}

// --- Attacker wave --------------------------------------------------------
// A base defense's attackers are `raider` enemies (tactical-enemy-ai): each
// marches on one exterior (egress) door, chips it down as a destructible field
// object, then floods into the room behind it. This assembles both halves from
// the base geometry: the door field objects and the raiders assigned to them.

// The world midpoint of a door edge (where it sits on the wall line).
function doorMidpointWorld(toWorld, edgeKey) {
  const { a, b } = latticeEndpoints(edgeKey);
  return toWorld((a[0] + b[0]) / 2, (a[1] + b[1]) / 2);
}

// Every door (exterior + interior) as a destructible field object, keyed by its
// edgeKey so the nav grid (which tags door cells with the same edgeKey) and the
// breach overlay both join to it. Interior doors are inert until a raider reaches
// them, but carry HP so they render + can be chipped generically.
export function baseDoorFieldObjects(shape, wallState, { doorHp = DEFAULT_DOOR_HP } = {}) {
  const toWorld = makeToWorld(shape);
  return (wallState?.doorEdgeKeys || []).map((edgeKey) => ({
    id: `door:${edgeKey}`,
    kind: "door",
    edgeKey,
    coordinate: doorMidpointWorld(toWorld, edgeKey),
    exterior: isExteriorEdge(cellsOf(shape), edgeKey),
    maxHp: doorHp,
    hp: doorHp,
  }));
}

function cellsOf(shape) {
  return shape.cells instanceof Set ? shape.cells : new Set(shape.cells || []);
}

// Scale a lon/lat delta so its metric length is ~distMeters, keeping the bearing
// (fine over the tens-of-metres a defense zone spans). Used to drop a raider on
// the zone ring along the line from the base centre through its target door.
function pointAtDistance(center, target, distMeters, fanRadians = 0) {
  let dx = target[0] - center[0];
  let dy = target[1] - center[1];
  if (fanRadians) {
    const c = Math.cos(fanRadians), s = Math.sin(fanRadians);
    [dx, dy] = [dx * c - dy * s, dx * s + dy * c];
  }
  const cur = distanceInMeters(center, [center[0] + dx, center[1] + dy]) || 1;
  const scale = distMeters / cur;
  return [center[0] + dx * scale, center[1] + dy * scale];
}

// The attacker wave for a base defense: `count` raiders spread round-robin over
// the exterior doors, each spawned out at the zone edge on its door's bearing
// (fanned when several share a door) and assigned that door + an interior goal.
// The goal is the `objective` (the safe room — what they're actually trying to
// reach) so a breached raider pushes deeper, chipping interior doors on the way,
// instead of idling at its entry door; it falls back to the cell just inside the
// door when no objective is given. Returns [] when the base has no exterior door.
export function buildRaiderWave(zone, shape, wallState, { count, objective = null, doorHp = DEFAULT_DOOR_HP, spawnRadiusFraction = 0.88, fanDegrees = 6 } = {}) {
  const cells = cellsOf(shape);
  const toWorld = makeToWorld(shape);
  const center = zone.centerCoordinate;
  const exteriorDoors = (wallState?.doorEdgeKeys || []).filter((edgeKey) => isExteriorEdge(cells, edgeKey));
  if (!exteriorDoors.length || !Array.isArray(center) || !Number.isFinite(count) || count <= 0) return [];

  const radius = (zone.radiusMeters ?? 120) * spawnRadiusFraction;
  const perDoor = new Map(); // edgeKey -> how many raiders already assigned (for fanning)
  const raiders = [];
  for (let i = 0; i < count; i += 1) {
    const edgeKey = exteriorDoors[i % exteriorDoors.length];
    const { a, b } = latticeEndpoints(edgeKey);
    const midLattice = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    const doorCoordinate = toWorld(midLattice[0], midLattice[1]);
    // Interior goal: the safe room if given, else the door midpoint nudged toward
    // whichever side cell is inside the base (so a breached raider heads in).
    const { sides } = doorwaySides(cells, edgeKey);
    const interiorDir = Object.entries(sides).find(([key]) => cells.has(key))?.[1] ?? [0, 0];
    const interiorCoordinate = Array.isArray(objective)
      ? [...objective]
      : toWorld(midLattice[0] + interiorDir[0] * 1.2, midLattice[1] + interiorDir[1] * 1.2);

    const share = perDoor.get(edgeKey) ?? 0;
    perDoor.set(edgeKey, share + 1);
    // Fan raiders sharing a door 0, -1, +1, -2, +2 ... slots off the bearing.
    const slot = share % 2 === 0 ? share / 2 : -(share + 1) / 2;
    const spawn = pointAtDistance(center, doorCoordinate, radius, (slot * fanDegrees * Math.PI) / 180);

    raiders.push({
      id: `raider:${i + 1}`,
      enemyType: ENEMY_TYPES.raider,
      behavior: ENEMY_STATES.wander,
      position: spawn,
      facingDegrees: facingBearingDegrees(spawn, doorCoordinate),
      speedMetersPerGameMinute: ENEMY_SPEED_METERS_PER_GAME_MINUTE,
      lastAdvancedAtGameMinute: 0,
      path: [[...spawn]],
      pathIndex: 0,
      assault: { doorObjectId: `door:${edgeKey}`, doorCoordinate, interiorCoordinate },
    });
  }
  return raiders;
}

// One call for both halves of the assault: the door field objects (all doors)
// and the raider wave (exterior doors only). Kept together so the door ids the
// raiders reference always match the field objects that exist. `objective` (the
// safe-room world coordinate) is the raiders' flood target.
export function buildBaseAssault(zone, shape, wallState, { count, objective = null, doorHp = DEFAULT_DOOR_HP } = {}) {
  return {
    fieldObjects: baseDoorFieldObjects(shape, wallState, { doorHp }),
    raiders: buildRaiderWave(zone, shape, wallState, { count, objective, doorHp }),
  };
}
